import React, { useMemo } from 'react';
import { View, Text } from '@tarojs/components';
import { FormulaNode, fractionFactors, parseFormula } from '@/utils/formula';
import styles from './index.module.scss';

const ungroup = (node: FormulaNode) => node.kind === 'group' ? node.child : node;
const Product: React.FC<{ factors: FormulaNode[]; inFraction?: boolean }> = ({ factors, inFraction }) => (
  <View className={styles.row}>
    {factors.map((factor, i) => <View key={i} className={styles.term}>
      {i > 0 && <Text className={styles.operator}>×</Text>}
      <Expression node={inFraction && factors.length === 1 ? ungroup(factor) : factor} />
    </View>)}
  </View>
);
const Expression: React.FC<{ node: FormulaNode }> = ({ node }) => {
  if (node.kind === 'number') return <Text>{node.text}</Text>;
  if (node.kind === 'group') return <View className={styles.term}><Text>(</Text><Expression node={node.child} /><Text>)</Text></View>;
  if (node.kind === 'negative') return <View className={styles.term}><Text>−</Text><Expression node={node.child} /></View>;
  if (node.kind === 'percent') return <View className={styles.term}><Expression node={node.child} /><Text>%</Text></View>;
  if (node.kind !== 'binary') return null;
  if (node.op === '*' || node.op === '/') {
    const { numerator, denominator } = fractionFactors(node);
    if (!denominator.length) return <Product factors={numerator} />;
    return <View className={styles.fraction} data-fraction='true'>
      <View className={styles.numerator}><Product factors={numerator} inFraction /></View>
      <View className={styles.denominator}><Product factors={denominator} inFraction /></View>
    </View>;
  }
  if (node.op === '^') return <View className={styles.term}>
    <Expression node={node.left} /><View className={styles.exponent}><Expression node={ungroup(node.right)} /></View>
  </View>;
  const terms: { op: string; node: FormulaNode }[] = [];
  const collect = (part: FormulaNode, op = '') => {
    if (part.kind === 'binary' && (part.op === '+' || part.op === '-')) {
      collect(part.left, op); terms.push({ op: part.op === '-' ? '−' : '+', node: part.right });
    } else terms.push({ op, node: part });
  };
  collect(node);
  return <View className={styles.row}>{terms.map((term, i) => <View key={i} className={styles.term}>
    {term.op && <Text className={styles.operator}>{term.op}</Text>}<Expression node={term.node} />
  </View>)}</View>;
};

export const Formula: React.FC<{ text: string }> = ({ text }) => {
  const relation = text.search(/[≈=]/), left = relation < 0 ? text : text.slice(0, relation);
  const node = useMemo(() => parseFormula(left), [left]);
  return <View className={styles.formula} data-math-source={text}>
    {node ? <><Expression node={node} />{relation >= 0 && <Text className={styles.relation}>{text.slice(relation)}</Text>}</> : <Text>{text}</Text>}
  </View>;
};
export const FormulaExplanation: React.FC<{ text: string }> = ({ text }) => <View>
  {text.split(/[；。]/).filter(Boolean).map((line, i) => {
    const start = line.search(/[\d(−-]/), prefix = start < 0 ? line : line.slice(0, start);
    return <View className={styles.proseLine} key={i}>{prefix && <Text>{prefix}</Text>}{start >= 0 && <Formula text={line.slice(start)} />}</View>;
  })}
</View>;
